import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { PenSquare, Search } from 'lucide-react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MessagesPage = () => {
    const [conversations, setConversations] = useState([]);
    const [loading, setLoading] = useState(true);
    const { user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        fetchConversations();
    }, []);

    const fetchConversations = async () => {
        try {
            const response = await axios.get(`${API}/messages/conversations`);
            setConversations(response.data);
        } catch (error) {
            console.error('Error fetching conversations:', error);
        } finally {
            setLoading(false);
        }
    };

    const getOtherParticipant = (conversation) => {
        return conversation.participants.find(p => p.id !== user.id);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="h-full flex flex-col bg-black" data-testid="messages-page">
            {/* Header */}
            <div className="flex-shrink-0 glass border-b border-white/10">
                <div className="max-w-md mx-auto flex items-center justify-between px-4 h-14">
                    <h1 className="font-heading text-2xl font-bold text-white tracking-tight">
                        Messages
                    </h1>
                    <button
                        onClick={() => navigate('/search')}
                        className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors"
                        data-testid="new-message-button"
                    >
                        <PenSquare className="w-5 h-5 text-white" />
                    </button>
                </div>
            </div>

            {/* Conversations List */}
            <div className="flex-1 overflow-y-auto overscroll-contain">
                <div className="max-w-md mx-auto pb-8" data-testid="conversations-list">
                {conversations.length === 0 ? (
                    <div className="py-20 text-center">
                        <div className="w-16 h-16 bg-surface rounded-full flex items-center justify-center mx-auto mb-4">
                            <PenSquare className="w-8 h-8 text-white/40" />
                        </div>
                        <p className="text-white/60 mb-2">No messages yet</p>
                        <p className="text-white/40 text-sm">Start a conversation by searching for users</p>
                        <button
                            onClick={() => navigate('/search')}
                            className="mt-4 text-primary font-medium"
                        >
                            Find people
                        </button>
                    </div>
                ) : (
                    conversations.map((conversation, index) => {
                        const otherUser = getOtherParticipant(conversation);
                        if (!otherUser) return null;

                        return (
                            <motion.button
                                key={conversation.id}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.05 }}
                                onClick={() => navigate(`/chat/${conversation.id}`)}
                                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors border-b border-border"
                                data-testid={`conversation-${conversation.id}`}
                            >
                                <Avatar className="w-12 h-12">
                                    <AvatarImage src={otherUser.avatar} alt={otherUser.username} />
                                    <AvatarFallback className="bg-primary/20 text-primary">
                                        {otherUser.username?.charAt(0).toUpperCase()}
                                    </AvatarFallback>
                                </Avatar>
                                <div className="flex-1 text-left">
                                    <div className="flex items-center justify-between">
                                        <span className="text-white font-medium">{otherUser.username}</span>
                                        {conversation.last_message_time && (
                                            <span className="text-white/40 text-xs">
                                                {formatDistanceToNow(new Date(conversation.last_message_time), { addSuffix: false })}
                                            </span>
                                        )}
                                    </div>
                                    {conversation.last_message && (
                                        <p className="text-white/60 text-sm truncate mt-0.5">
                                            {conversation.last_message}
                                        </p>
                                    )}
                                </div>
                            </motion.button>
                        );
                    })
                )}
                </div>
            </div>
        </div>
    );
};

export default MessagesPage;
